import { defineStore } from "pinia";
import { getNewsInfo } from "@/api/getInfo";
import {
  createPersistenceConfig,
  readCache,
  writeCache,
  clearCache,
  isCacheAvailable,
  isCacheExpiring,
  getCacheInfo,
  type CacheInfo,
} from "@/utils/cache.ts";

// ========== 持久化配置 ==========
const PERSISTENCE_CONFIG = createPersistenceConfig("newsList");

export type NewsType = {
  category: string;
  comments: number;
  content: string;
  date: string;
  excerpt: string;
  id: number;
  image: string;
  isHot: boolean;
  rank: number;
  status: number;
  title: string;
  views: number;
};

interface NewsState {
  newsList: NewsType[];
}

export const useNewsStore = defineStore("news", {
  state: (): NewsState => ({
    newsList: [],
  }),
  getters: {
    getNewsList: (state) => state.newsList,
  },
  actions: {
    async fetchNewsList(
      forceRefresh = false,
      silentRefresh = false,
    ): Promise<void> {
      // 1. 检查内存缓存
      if (this.newsList.length > 0 && !forceRefresh) {
        return;
      }

      // 2. 检查 localStorage 缓存
      if (!forceRefresh) {
        const cachedData = readCache<NewsType[]>(PERSISTENCE_CONFIG);

        if (cachedData && Array.isArray(cachedData)) {
          this.newsList = cachedData;

          // 静默刷新：缓存即将过期时后台异步刷新
          if (silentRefresh && isCacheExpiring(PERSISTENCE_CONFIG)) {
            this.refreshInBackground();
          }

          return;
        }
      }

      // 3. 请求接口
      try {
        const response = await getNewsInfo();
        const data = response?.data;

        if (!this.isValidNewsArray(data)) {
          throw new Error("接口返回数据格式异常");
        }

        this.newsList = data;
        writeCache(PERSISTENCE_CONFIG, data);
      } catch (error) {
        // 降级策略
        if (isCacheAvailable(PERSISTENCE_CONFIG)) {
          const fallbackData = readCache<NewsType[]>(PERSISTENCE_CONFIG);
          if (fallbackData && Array.isArray(fallbackData)) {
            this.newsList = fallbackData;
            return;
          }
        }

        if (this.newsList.length === 0) {
          throw error;
        }
      }
    },

    async refreshInBackground(): Promise<void> {
      try {
        const response = await getNewsInfo();
        const data = response?.data;

        if (this.isValidNewsArray(data)) {
          this.newsList = data;
          writeCache(PERSISTENCE_CONFIG, data);
        }
      } catch (error) {}
    },

    async refreshNews(): Promise<void> {
      return this.fetchNewsList(true, false);
    },

    clearCache(): void {
      this.newsList = [];
      clearCache(PERSISTENCE_CONFIG);
    },

    getCacheInfo(): CacheInfo {
      return getCacheInfo(PERSISTENCE_CONFIG);
    },

    isValidNewsArray(data: unknown): data is NewsType[] {
      return (
        Array.isArray(data) &&
        data.every(
          (item) =>
            typeof item === "object" &&
            item !== null &&
            "id" in item &&
            typeof (item as NewsType).id === "number",
        )
      );
    },

    updateNews(updatedNews: NewsType): void {
      const index = this.newsList.findIndex((n) => n.id === updatedNews.id);
      if (index !== -1) {
        this.newsList[index] = { ...this.newsList[index], ...updatedNews };
        writeCache(PERSISTENCE_CONFIG, this.newsList);
      }
    },
  },
});
